import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import AssignmentForm from '@/components/AssignmentForm';
import GradeForm from '@/components/GradeForm';
import QuizEditor from '@/components/QuizEditor';
import QuizTaker from '@/components/QuizTaker';
import Seo from '@/components/Seo';
import StudentSubmitForm from '@/components/StudentSubmitForm';
import { getCourse, getInstructor } from '@/data/catalog';
import { useAuth } from '@/hooks/useAuth';
import {
  deleteCourseAssignment,
  fetchCourseAssignments,
  kindLabel,
  type CourseAssignment } from
'@/lib/assignments';
import { fetchMyEnrollment, statusLabel, type CourseEnrollment } from '@/lib/enrollments';
import { getEnrollment, progressPercent } from '@/lib/progress';
import {
  fetchCourseSubmissions,
  latestSubmissionFor,
  submissionLabel,
  type AssignmentSubmission } from
'@/lib/submissions';

function CourseHome() {
  const { slug = '' } = useParams();
  const course = getCourse(slug);
  const { isAuthenticated, isAdmin, user } = useAuth();
  const [tab, setTab] = useState<'modulos' | 'tareas'>('modulos');
  const [request, setRequest] = useState<CourseEnrollment | undefined>();
  const [assignments, setAssignments] = useState<CourseAssignment[]>([]);
  const [submissions, setSubmissions] = useState<AssignmentSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const userId = user?.userId;
    const courseId = course?.id;
    if (!userId || !courseId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
    fetchMyEnrollment(userId, courseId),
    fetchCourseAssignments(courseId),
    fetchCourseSubmissions(courseId)]
    ).
    then(([enrollment, list, work]) => {
      if (cancelled) return;
      setRequest(enrollment);
      setAssignments(list);
      setSubmissions(work);
    }).
    catch((err) => {
      console.error('No se pudo abrir el curso:', err);
      if (!cancelled) setError('No se pudo cargar este curso. Inténtalo de nuevo.');
    }).
    finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [user?.userId, course?.id]);

  if (!course) {
    return <Navigate to="/panel" replace />;
  }

  if (!isAuthenticated) {
    return <Navigate to={`/acceso?next=/panel/${course.slug}`} replace />;
  }

  const instructor = getInstructor(course.instructorId);
  const local = user?.userId ? getEnrollment(user.userId, course.id) : undefined;
  const percent = progressPercent(local, course.modules.length);
  const enrolled = request?.status === 'approved';
  const openDocs = enrolled && request?.documentsAccess;
  const canView = isAdmin || Boolean(request);

  const removeAssignment = async (id: string) => {
    setError('');
    try {
      await deleteCourseAssignment(id);
      setAssignments((list) => list.filter((item) => item._id !== id));
    } catch (err) {
      console.error('No se pudo eliminar la actividad:', err);
      setError('No se pudo eliminar. Comprueba que tu cuenta es administradora.');
    }
  };

  const replaceSubmission = (record: AssignmentSubmission) => {
    setSubmissions((list) => {
      const rest = list.filter((item) => item._id !== record._id);
      return [record, ...rest];
    });
  };

  const replaceAssignment = (record: CourseAssignment) => {
    setAssignments((list) => list.map((item) => item._id === record._id ? record : item));
  };

  return (
    <>
      <Seo title={`${course.title} · Cuenta`} description={`Módulos y assignments de ${course.title} en HFCursos.`} />
      <section className="mx-auto max-w-6xl px-5 py-12 lg:px-8">
        <Link to="/panel" className="text-xs uppercase tracking-[0.28em] text-[#C41E3A]">
          Inicio de cuenta
        </Link>
        <h1 className="mt-3 font-['Fraunces'] text-4xl">{course.title}</h1>
        <p className="mt-2 text-sm text-[#1E3A5F]/65">
          {instructor?.name || 'HFCursos'} · {course.duration}
        </p>

        {loading ?
        <p className="mt-10 text-sm text-[#1E3A5F]/60">Cargando tu curso…</p> :
        !canView ?
        <div className="mt-10 border border-dashed border-[#1E3A5F]/20 p-8">
            <p className="font-['Fraunces'] text-2xl">Aún no estás inscrito</p>
            <p className="mt-2 text-sm text-[#1E3A5F]/65">Solicita plaza desde la ficha para ver módulos y assignments.</p>
            <Link to={`/cursos/${course.slug}`} className="mt-5 inline-block bg-[#1E3A5F] px-4 py-2 text-sm text-[#F4EFE6]">
              Ir a la ficha
            </Link>
          </div> :

        <>
            <p className="mt-4 text-sm text-[#1E3A5F]/70">
              {isAdmin ? 'Vista de administración del curso.' : statusLabel(request?.status)}
              {request && !isAdmin ? ` · ${request.documentsAccess ? 'documentos abiertos' : 'documentos pendientes'}` : ''}
            </p>
            {enrolled || isAdmin ?
          <p className="mt-2 text-sm text-[#1E3A5F]/60">Progreso {percent}%</p> :
          null}

            <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label="Secciones del curso">
              <button
              type="button"
              role="tab"
              aria-selected={tab === 'modulos'}
              onClick={() => setTab('modulos')}
              className={`border px-3 py-1.5 text-sm ${
              tab === 'modulos' ? 'border-[#1E3A5F] bg-[#1E3A5F] text-[#F4EFE6]' : 'border-[#1E3A5F]/20'}`
              }>

                Módulos
              </button>
              <button
              type="button"
              role="tab"
              aria-selected={tab === 'tareas'}
              onClick={() => setTab('tareas')}
              className={`border px-3 py-1.5 text-sm ${
              tab === 'tareas' ? 'border-[#1E3A5F] bg-[#1E3A5F] text-[#F4EFE6]' : 'border-[#1E3A5F]/20'}`
              }>

                Assignments
              </button>
            </div>

            {error ? <p className="mt-4 text-sm text-[#C41E3A]">{error}</p> : null}

            {tab === 'modulos' ?
          <ol className="mt-8 space-y-3">
                {course.modules.map((item, index) => {
              const done = local?.completedModules.includes(item.id);
              const count = assignments.filter((task) => task.moduleId === item.id).length;
              return (
                <li key={item.id} className="border border-[#1E3A5F]/10 p-5">
                      <p className="text-xs uppercase tracking-wide text-[#C41E3A]">
                        Sesión {index + 1} · {item.duration}
                        {done ? ' · hecha' : ''}
                      </p>
                      <h2 className="mt-1 font-['Fraunces'] text-2xl">{item.title}</h2>
                      <p className="mt-2 text-sm leading-6 text-[#1E3A5F]/70">{item.summary}</p>
                      <p className="mt-2 text-xs text-[#1E3A5F]/50">
                        {count === 1 ? '1 assignment' : `${count} assignments`}
                      </p>
                      {openDocs || isAdmin ?
                  <Link to={`/aula/${course.slug}?sesion=${index}`} className="mt-4 inline-block text-sm underline">
                          Abrir módulo
                        </Link> :

                  <p className="mt-4 text-xs text-[#1E3A5F]/50">
                          El aula se abre cuando un administrador te dé acceso a documentos.
                        </p>
                  }
                    </li>);

            })}
              </ol> :

          <div className="mt-8 space-y-8">
                {course.modules.map((module, index) => {
              const moduleTasks = assignments.filter((item) => item.moduleId === module.id);
              return (
                <section key={module.id} className="border border-[#1E3A5F]/10 p-5">
                      <p className="uppercase tracking-wide text-[#C41E3A] text-sm font-medium">Módulo {index + 1}</p>
                      <h2 className="mt-1 font-['Fraunces'] text-2xl">{module.title}</h2>

                      {moduleTasks.length === 0 ?
                  <p className="mt-4 text-sm text-[#1E3A5F]/60">Todavía no hay assignments en este módulo.</p> :

                  <ul className="mt-4 space-y-3">
                          {moduleTasks.map((item) => {
                      const mine = user?.userId ?
                      latestSubmissionFor(submissions, item._id, user.userId) :
                      undefined;
                      const allForTask = submissions.filter((row) => row.assignmentId === item._id);
                      return (
                        <li key={item._id} className="border border-[#1E3A5F]/10 p-4">
                                <p className="text-xs uppercase tracking-wide text-[#C41E3A]">
                                  {kindLabel(item.kind)}
                                  {item.dueLabel ? ` · ${item.dueLabel}` : ''}
                                </p>
                                <h3 className="mt-1 font-['Fraunces'] text-xl">{item.title}</h3>
                                {item.description ?
                          <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-[#1E3A5F]/70">
                                    {item.description}
                                  </p> :
                          null}
                                <div className="mt-3 flex flex-wrap gap-3">
                                  {item.externalUrl ?
                            <a href={item.externalUrl} target="_blank" rel="noreferrer" className="text-sm underline">
                                      Abrir liga
                                    </a> :
                            null}
                                  {item.fileUrl ?
                            <a href={item.fileUrl} target="_blank" rel="noreferrer" className="text-sm underline">
                                      Ver archivo{item.fileName ? `: ${item.fileName}` : ''}
                                    </a> :
                            null}
                                  {isAdmin ?
                            <button
                              type="button"
                              onClick={() => void removeAssignment(item._id)}
                              className="text-sm text-[#C41E3A]">

                                      Quitar
                                    </button> :
                            null}
                                </div>

                                {isAdmin && item.kind === 'quiz' ?
                          <QuizEditor assignment={item} onSaved={replaceAssignment} /> :
                          null}

                                {!isAdmin && user?.userId ?
                          mine ?
                          <div className="mt-4 text-sm text-[#1E3A5F]/70">
                                      <p>
                                        {submissionLabel(mine.status)}
                                        {mine.score != null ? ` · nota ${mine.score}` : ''}
                                      </p>
                                      {mine.fileUrl ?
                            <a href={mine.fileUrl} target="_blank" rel="noreferrer" className="mt-1 inline-block underline">
                                          Tu archivo{mine.fileName ? `: ${mine.fileName}` : ''}
                                        </a> :
                            null}
                                      {mine.feedback ? <p className="mt-2">{mine.feedback}</p> : null}
                                    </div> :
                          enrolled ?
                          item.kind === 'quiz' ?
                          <QuizTaker
                            assignment={item}
                            courseId={course.id}
                            courseSlug={course.slug}
                            moduleId={module.id}
                            userId={user.userId}
                            studentName={user.userName || request?.fullName || 'Estudiante'}
                            studentEmail={user.email || request?.email || ''}
                            onCreated={(record) => setSubmissions((list) => [record, ...list])} /> :

                          <StudentSubmitForm
                            assignmentId={item._id}
                            courseId={course.id}
                            courseSlug={course.slug}
                            moduleId={module.id}
                            userId={user.userId}
                            studentName={user.userName || request?.fullName || 'Estudiante'}
                            studentEmail={user.email || request?.email || ''}
                            onCreated={(record) => setSubmissions((list) => [record, ...list])} /> :


                          <p className="mt-4 text-xs text-[#1E3A5F]/50">
                                      Podrás entregar cuando tu inscripción esté aceptada.
                                    </p> :

                          null}

                                {isAdmin ?
                          <div className="mt-4">
                                    {allForTask.length === 0 ?
                            <p className="text-xs text-[#1E3A5F]/50">Aún no hay entregas.</p> :

                            <ul className="space-y-3">
                                        {allForTask.map((row) =>
                              <li key={row._id} className="border border-[#1E3A5F]/10 p-3">
                                            <p className="text-sm">
                                              {row.studentName} · {submissionLabel(row.status)}
                                              {row.score != null ? ` · ${row.score}` : ''}
                                            </p>
                                            {row.studentEmail ?
                                <p className="text-xs text-[#1E3A5F]/50">{row.studentEmail}</p> :
                                null}
                                            {row.note ? <p className="mt-1 text-sm text-[#1E3A5F]/70">{row.note}</p> : null}
                                            {row.fileUrl ?
                                <a
                                  href={row.fileUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="mt-1 inline-block text-sm underline">

                                                Ver entrega{row.fileName ? `: ${row.fileName}` : ''}
                                              </a> :
                                null}
                                            <GradeForm submission={row} onGraded={replaceSubmission} />
                                          </li>
                              )}
                                      </ul>
                            }
                                  </div> :
                          null}
                              </li>);

                    })}
                        </ul>
                  }

                      {isAdmin && user?.userId ?
                  <AssignmentForm
                    course={course}
                    creator={user.userId}
                    defaultModuleId={module.id}
                    onCreated={(record) => setAssignments((list) => [record, ...list])} /> :

                  null}
                    </section>);

            })}
              </div>
          }
          </>
        }
      </section>
    </>);

}

export default CourseHome;